// --- src/components/ForgotPassword.tsx ---
import React, { useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Mail, ArrowLeft, Loader2, CheckCircle, Lock } from 'lucide-react';
import { Toast } from './Toast';

interface ForgotPasswordProps {
  onBack: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const SYSTEM_DOMAIN = 'example.com';

  // Get the redirect URL (should point to UpdatePassword page)
  const getRedirectUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/#update-password`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    // Construct email: if it doesn't contain @, append @example.com
    const emailToUse = email.includes('@') ? email.trim() : `${email.trim()}@${SYSTEM_DOMAIN}`;

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(emailToUse, {
        redirectTo: getRedirectUrl(),
      });

      if (error) throw error;

      setSuccess(true);
      setToast({
        message: 'Ha létezik ilyen fiók, egy visszaállítási linket küldtünk az email címére.',
        type: 'success',
      });
    } catch (error: any) {
      setToast({
        message: error.message || 'Hiba történt. Kérlek próbáld újra.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--color-background, #f8fafc)' }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-slate-200 dark:border-gray-700">
        <div className="bg-slate-900 dark:bg-gray-950 p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-blue-900/50">
            <Lock className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Jelszó visszaállítása</h1>
          <p className="text-slate-400 text-sm mt-2">Add meg az email címed</p>
        </div>

        <div className="p-8">
          {success ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="text-green-600 dark:text-green-400" size={32} />
              </div>
              <p className="text-gray-700 dark:text-gray-300">
                Ha létezik ilyen fiók, egy visszaállítási linket küldtünk az email címére.
              </p>
              <button
                onClick={onBack}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
              >
                <ArrowLeft size={18} />
                Vissza a bejelentkezéshez
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 dark:text-gray-400 uppercase">
                    Felhasználónév vagy email cím
                  </label>
                  <div className="relative">
                    <Mail
                      className="absolute left-3 top-3 text-slate-400 dark:text-gray-500"
                      size={18}
                    />
                    <input
                      type="text"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="pl. Raktar01 vagy Raktar01@example.com"
                      required
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Add meg a felhasználóneved vagy a teljes email címed
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      <span>Küldés...</span>
                    </>
                  ) : (
                    <>
                      <Mail size={18} />
                      <span>Visszaállítási link küldése</span>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={onBack}
                  className="text-sm text-slate-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition flex items-center justify-center gap-1 mx-auto"
                >
                  <ArrowLeft size={16} />
                  Vissza a bejelentkezéshez
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default ForgotPassword;
